import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, ShieldCheck, FileText, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const OrgCard = ({ o, i }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ delay: i * 0.05 }}
    className="card-surface rounded-xl p-6" data-testid={`source-org-${i}`}>
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/15 flex items-center justify-center shrink-0 font-display font-bold text-xl text-[#00F0FF]">{o.initial}</div>
      <div className="min-w-0">
        <h3 className="font-display font-bold text-lg">{o.name}</h3>
        <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{o.description}</p>
        <div className="mt-4 space-y-2 text-sm">
          <p><span className="text-[#64748B] font-mono-tech text-xs uppercase">Data we use: </span><span className="text-[#e2e8f0]">{o.data_we_use}</span></p>
          <p><span className="text-[#64748B] font-mono-tech text-xs uppercase">Report: </span><span className="text-[#e2e8f0]">{o.report_name} — {o.date}</span></p>
          <p><span className="text-[#64748B] font-mono-tech text-xs uppercase">Publishes: </span><span className="text-[#e2e8f0]">{o.publishes}</span></p>
          <p className="flex items-center gap-1.5 text-[#4ade80]"><CheckCircle2 className="w-3.5 h-3.5" /> Last verified by WorldAIInfo: {o.last_verified}</p>
        </div>
        {o.note && <p className="text-xs text-[#64748B] italic mt-3 border-l-2 border-white/10 pl-3">{o.note}</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          <a href={o.report_url} target="_blank" rel="noopener noreferrer" data-testid={`source-report-${i}`}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#00F0FF] text-[#04121a] font-semibold hover:brightness-110">
            View Report <ExternalLink className="w-3 h-3" />
          </a>
          {o.pdf_url && (
            <a href={o.pdf_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-white/15 text-[#94A3B8] hover:border-[#00F0FF]/50 hover:text-[#00F0FF]">
              Download PDF <FileText className="w-3 h-3" />
            </a>
          )}
          {o.archived_url && (
            <a href={o.archived_url} target="_blank" rel="noopener noreferrer" data-testid={`source-archive-${i}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-white/15 text-[#94A3B8] hover:border-[#00F0FF]/50 hover:text-[#00F0FF]">
              Archived Copy <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

export default function Sources() {
  const [data, setData] = useState({ organisations: [], methodology: [], verified_date: "" });

  useEffect(() => {
    api.get("/sources").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="sources-page">
      <Seo title="Data Sources & Methodology — WorldAIInfo" description="Every statistic on WorldAIInfo is cited. See our data sources — Tortoise Global AI Index, Stanford HAI, Epoch AI and more — and our verification methodology." canonicalPath="/sources" />
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-28 pb-20">
        <div className="inline-flex items-center gap-2 border border-[#00F0FF]/30 rounded-full px-4 py-1.5 mb-6 bg-[#00F0FF]/5">
          <ShieldCheck className="w-4 h-4 text-[#00F0FF]" />
          <span className="font-mono-tech text-xs text-[#00F0FF]">VERIFIED & CITED · {data.verified_date}</span>
        </div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-tight">Data Sources & Methodology</h1>
        <p className="mt-6 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-3xl">
          Every statistic on WorldAIInfo.com is sourced from verified research organisations. We cite specifically —
          showing the exact report, organisation, and date for every number we publish. Below is our complete source documentation.
        </p>

        <div className="mt-12 space-y-5">
          {data.organisations.map((o, i) => <OrgCard key={i} o={o} i={i} />)}
        </div>

        <div className="mt-16">
          <h2 className="font-display font-bold text-2xl mb-6">Our Methodology</h2>
          <div className="space-y-4">
            {data.methodology.map((m, i) => (
              <div key={i} className="card-surface rounded-xl p-5" data-testid={`methodology-${i}`}>
                <h3 className="font-display font-bold text-base text-[#00F0FF]">{m.q}</h3>
                <p className="text-sm text-[#94A3B8] mt-1.5 leading-relaxed">{m.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
